import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Search, FileText } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-500 dark:bg-ink-panel dark:text-slate-400",
  sent: "bg-sky-50 text-sky-700 dark:bg-ink-gold/10 dark:text-ink-gold",
  accepted: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  rejected: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  cancelled: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  expired: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
};
const INPUT = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text";
const money = (cur, v) => `${cur} ${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

export default function Quotations() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (statusFilter) params.status = statusFilter;
      const r = await api.get("/quotations", { params });
      setRows(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, statusFilter]); // eslint-disable-line

  return (
    <div className="space-y-6" data-testid="quotations-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Sales Pipeline</p>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text">Quotations</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Customer-facing commercial documents. Create one from an inquiry with a saved costing.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-56 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search quotation number…" data-testid="quotation-search-input" className={`${INPUT} pl-9`} />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="quotation-status-filter" className={`${INPUT} w-auto`}>
          <option value="">All statuses</option>
          {["draft", "sent", "accepted", "rejected", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600 dark:text-ink-gold" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center dark:border-ink-line" data-testid="quotations-empty-state">
          <FileText className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">No quotations yet</p>
          <p className="mt-1 text-xs text-slate-400">Open an inquiry, save its costing, then create a quotation.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-ink-line dark:bg-ink-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="quotations-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 dark:border-ink-line dark:bg-ink/40">
                  <th className="px-5 py-3 font-medium">Quotation #</th>
                  <th className="px-5 py-3 font-medium">Customer</th>
                  <th className="px-5 py-3 font-medium">Inquiry</th>
                  <th className="px-5 py-3 font-medium">Total</th>
                  <th className="px-5 py-3 font-medium">Valid until</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => navigate(`/quotations/${r.id}`)} className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50 dark:border-ink-line dark:hover:bg-ink-panel/40" data-testid={`quotation-row-${r.quotation_number}`}>
                    <td className="px-5 py-3 font-mono-tf text-xs font-semibold text-slate-700 dark:text-ink-gold">{r.revision_label}</td>
                    <td className="px-5 py-3 font-medium text-slate-800 dark:text-ink-text">{r.customer_name || "—"}</td>
                    <td className="px-5 py-3 font-mono-tf text-xs text-slate-600 dark:text-slate-300">{r.inquiry_number || "—"}</td>
                    <td className="px-5 py-3 font-mono-tf text-slate-700 dark:text-slate-200" data-testid={`quotation-total-${r.quotation_number}`}>{money(r.currency, r.customer_total)}</td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{r.valid_until || "—"}</td>
                    <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[r.display_status] || STATUS_STYLES.draft}`} data-testid={`quotation-status-${r.quotation_number}`}>{r.display_status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
